/**
 * Blog content for podus.org — the Podus product blog.
 *
 * Written in Podus's own first-party voice ("we", "our lawyers"). Every factual claim reflects real,
 * publicly known facts about Podus and Citizens' Gavel (pro bono lawyer network, access-to-justice
 * interventions, Justice Clock) — keep it accurate; do not invent statistics or quotes.
 *
 * These are real, visible, indexable articles: the target topics live in genuine content that can
 * rank and earn links (never hidden keyword text).
 */

export type Block =
    | { type: 'p'; html: string }
    | { type: 'h2'; text: string }
    | { type: 'ul'; items: string[] }
    | { type: 'quote'; text: string; cite: string };

export interface Post {
    slug: string;
    title: string;
    /** ~150-160 char meta description. */
    description: string;
    /** ISO date, used for <time> and Article schema. */
    date: string;
    readingTime: string;
    tags: string[];
    /** One-line teaser for the blog index card. */
    excerpt: string;
    content: Block[];
}

export const POSTS: Post[] = [
    {
        slug: 'meet-podus-ai-legal-assistant',
        title: 'Meet Podus AI: The AI Legal Assistant Inside Your Podus Account',
        description:
            'Podus AI is a free AI legal assistant trained on Nigerian law. Here is what it does, how to use it over WhatsApp, and when to reach a human lawyer instead.',
        date: '2026-07-22',
        readingTime: '5 min read',
        tags: ['Podus AI', 'AI Legal Assistant', 'Legal Technology', 'Access to Justice'],
        excerpt:
            'Podus AI is the AI legal assistant at the heart of the Podus super app — trained on Nigerian law, free to use, and one message away when you need it.',
        content: [
            {
                type: 'p',
                html: 'Most people do not need a courtroom. They need an answer. Is this eviction legal? What do I say if I am stopped by the police? How do I write a complaint that an agency will actually read? These are the everyday legal questions that decide real outcomes — and they are exactly what <strong>Podus AI</strong> was built to answer.',
            },
            {
                type: 'p',
                html: 'Podus AI is the AI legal assistant at the heart of the Podus super app. It gives you fast, free legal guidance in plain language, and — when a matter needs more than an answer — connects you to the rest of what Podus offers, including real lawyers.',
            },
            { type: 'h2', text: 'Trained on Nigerian law, not the whole internet' },
            {
                type: 'p',
                html: 'What makes Podus AI different from a general chatbot is what it is trained on. Rather than generic web text, it is grounded in the overwhelming majority of Nigerian law, with support for UK and Ghanaian law too. That means the guidance you get reflects the rules that actually apply where you live — not a best guess from another jurisdiction.',
            },
            { type: 'h2', text: 'How to use it' },
            {
                type: 'p',
                html: 'We built Podus AI to meet you where you already are. You can use it for free over WhatsApp, on the web, and through our mobile app — no legal jargon required. You describe your situation in ordinary words, and Podus AI helps you understand your rights and take the next step.',
            },
            { type: 'p', html: 'In practice, people use Podus AI to:' },
            {
                type: 'ul',
                items: [
                    'Understand their rights before spending money on a lawyer',
                    'Draft petitions and complaint letters to the bodies that can act',
                    'Get plain-language guidance in an emergency, such as an arrest or detention',
                    'Figure out whether a matter needs a human lawyer — and get connected to one',
                ],
            },
            { type: 'h2', text: 'Built for the moments that matter most' },
            {
                type: 'p',
                html: 'Podus AI is deliberately tuned to prioritise urgent situations — police extortion and brutality, illegal arrest and detention, and other threats to a person’s fundamental rights. In those moments, a fast, clear first answer can be the difference between action and silence.',
            },
            { type: 'h2', text: 'AI and lawyers, working together' },
            {
                type: 'p',
                html: 'Podus AI is not here to replace lawyers — it is here to reach the people lawyers cannot. It handles the first, high-volume layer of legal questions so that human advocates can focus on the cases that truly need them. And because it lives inside your Podus Account, the same place you can access pro bono legal aid, contracts and more, moving from an AI answer to a human lawyer is seamless.',
            },
            {
                type: 'p',
                html: 'That is what the Podus super app is for: one account, from the first question to the final resolution. Try Podus AI free at <a href="https://chat.podus.ai" target="_blank" rel="noopener">chat.podus.ai</a>.',
            },
        ],
    },
    {
        slug: 'pro-bono-legal-aid-nigeria',
        title: 'Pro Bono Legal Aid in Nigeria: How Free Legal Intervention Works',
        description:
            'How pro bono legal aid works in Nigeria — free legal support, volunteer lawyers and legal intervention for indigent citizens who cannot afford a legal fee.',
        date: '2026-07-21',
        readingTime: '6 min read',
        tags: ['Pro Bono', 'Legal Aid', 'Legal Intervention', 'Access to Justice'],
        excerpt:
            'For too many Nigerians, the biggest barrier to justice is the legal fee. Here is how pro bono legal aid — free legal support from volunteer lawyers — closes that gap.',
        content: [
            {
                type: 'p',
                html: 'In Nigeria, the law is meant to protect everyone equally. In practice, it protects the people who can afford it. The moment a dispute becomes serious, the first question most people face is not “what are my rights?” but “can I pay the <strong>legal fee</strong>?” For millions of <strong>indigent citizens</strong>, the honest answer is no — and so the case is dropped before it begins.',
            },
            {
                type: 'p',
                html: 'Closing that gap is why Podus and <a href="https://podus.org" target="_blank" rel="noopener">Citizens’ Gavel</a> exist. Through <strong>pro bono legal aid</strong> — free legal support from lawyers who volunteer their time — we help people get a real <strong>legal intervention</strong> when they would otherwise have no one to call.',
            },
            { type: 'h2', text: 'What “pro bono” actually means for you' },
            {
                type: 'p',
                html: 'Pro bono is short for <em>pro bono publico</em> — “for the public good.” A <strong>pro bono case</strong> is one a lawyer takes on without charging the client. For the person on the other side, it means the same professional representation a paying client would get, at no cost. Our network of <strong>volunteer lawyers</strong> takes on <strong>pro bono court cases</strong> and out-of-court matters across the country.',
            },
            {
                type: 'p',
                html: 'A <strong>pro bono legal intervention</strong> through Podus can mean any of the following:',
            },
            {
                type: 'ul',
                items: [
                    'A lawyer stepping in when someone is arrested or detained without proper cause',
                    'Drafting and filing a formal complaint or petition on a citizen’s behalf',
                    'Representation or advice in ongoing court cases where the person cannot afford counsel',
                    'Connecting a citizen to an attorney of law who handles their specific type of matter',
                ],
            },
            { type: 'h2', text: 'From “I need help” to a lawyer on your side' },
            {
                type: 'p',
                html: 'The hardest part of getting help has always been knowing where to start. We built Podus so that the first step is simple: describe your situation, and we point you to the right support — including how to <strong>call a lawyer</strong> and find <strong>lawyers nearby</strong> who can act. Where a matter qualifies, we assign it to a pro bono lawyer from our network.',
            },
            {
                type: 'p',
                html: 'This is <strong>online legal aid</strong> built for how people actually reach out — over the web, over WhatsApp, in plain language. You do not need to know the correct legal term for your problem. You describe what happened; we help translate it into the right <strong>legal aid</strong> pathway.',
            },
            { type: 'h2', text: 'Why free legal support matters' },
            {
                type: 'p',
                html: 'When people can access <strong>free legal support</strong>, outcomes change. A tenant facing an unlawful eviction can push back. A trader detained over a civil debt can be released. A family that would have walked away from a valid claim can pursue it. Access to justice should not depend on the size of someone’s bank account — and pro bono legal aid is one of the most direct ways to make that true.',
            },
            {
                type: 'p',
                html: 'If you need help, start at <a href="https://podus.org" target="_blank" rel="noopener">podus.org</a>. If you are a lawyer who wants to take on pro bono cases, we would like to hear from you.',
            },
        ],
    },
    {
        slug: 'legal-emergency-police-station-rights-nigeria',
        title: 'Arrested in Nigeria? Your Rights at the Police Station — and How to Get Help Fast',
        description:
            'A legal emergency at a police station is frightening. Know your rights, when to call a lawyer, and how Podus connects you to free legal support in Nigeria.',
        date: '2026-07-21',
        readingTime: '6 min read',
        tags: ['Human Rights', 'Legal Emergency', 'Advocacy', 'Access to Justice'],
        excerpt:
            'Few moments are more frightening than a legal emergency at a police station. Here is what your rights are, and how to reach a lawyer when every minute counts.',
        content: [
            {
                type: 'p',
                html: 'A <strong>legal emergency</strong> rarely gives warning. One moment life is normal; the next, someone you love is being held at a <strong>police station</strong>, and no one in the family knows what to do. In those first hours, the difference between a quick release and a long, unlawful detention often comes down to one thing: whether you can reach a lawyer.',
            },
            {
                type: 'p',
                html: 'This is exactly the moment Podus was built for. We help people turn fear into a clear next step — including how to <strong>call a lawyer</strong> and get <strong>free legal support</strong> when it matters most.',
            },
            { type: 'h2', text: 'Know your rights the moment you are detained' },
            {
                type: 'p',
                html: 'Under the Nigerian Constitution and the Administration of Criminal Justice Act, a person who is arrested has rights that the police are bound to respect. In plain terms:',
            },
            {
                type: 'ul',
                items: [
                    'You have the right to remain silent and to avoid self-incrimination.',
                    'You have the right to be informed of the reason for your arrest, in a language you understand.',
                    'You have the right to a lawyer, and to contact your family or a relation.',
                    'You should be charged to court within a constitutionally reasonable time — not held indefinitely.',
                    'Bail for ordinary offences is a right, and at the police level it is meant to be free.',
                ],
            },
            {
                type: 'p',
                html: 'Knowing these rights is the first form of protection. Enforcing them is where a lawyer comes in.',
            },
            { type: 'h2', text: 'The safety net Nigeria has been missing' },
            {
                type: 'p',
                html: 'In some countries, anyone held at a station can ask for a <strong>duty solicitor</strong> — a <strong>legal aid solicitor</strong> on call to advise detainees for free. Nigeria has no widespread <strong>police duty solicitor</strong> scheme, so most people are left to face the station alone. Podus exists to fill that gap: a way to reach a <strong>volunteer lawyer</strong> quickly, so no one has to navigate a detention without advice.',
            },
            { type: 'h2', text: 'When a legal emergency becomes a human rights matter' },
            {
                type: 'p',
                html: 'Not every detention is lawful. Extortion, brutality, and detention without charge are not just wrong — they are <strong>human rights abuse</strong>. Our work has always prioritised these cases, and <strong>human rights advocacy</strong> is central to what Podus and Citizens’ Gavel do. Where a person’s fundamental rights have been violated, we help document what happened and pursue the right complaint against those responsible.',
            },
            {
                type: 'p',
                html: 'That combination — fast help in the moment, and <strong>advocacy</strong> afterwards — is how a single frightening night can end in <strong>justice</strong> rather than silence.',
            },
            {
                type: 'p',
                html: 'If you or someone you know is facing a legal emergency, reach us at <a href="https://podus.org" target="_blank" rel="noopener">podus.org</a>.',
            },
        ],
    },
    {
        slug: 'tech-justice-nigeria-judicial-system',
        title: 'Tech Justice: Using Technology to Open Up Nigeria’s Judicial System',
        description:
            'Tech justice means using technology to widen access to Nigeria’s judicial system — from digital legal advocacy to filing a legal complaint online.',
        date: '2026-07-21',
        readingTime: '5 min read',
        tags: ['Tech Justice', 'Digital Legal Advocacy', 'Judicial System', 'Access to Justice'],
        excerpt:
            'Nigeria’s courts are slow, distant and expensive to reach. Tech justice — technology built for access, not profit — is changing who the system actually serves.',
        content: [
            {
                type: 'p',
                html: 'Nigeria’s <strong>judicial system</strong> is capable and principled, but it is also slow, physically distant, and expensive to navigate. For an ordinary person, going to <strong>court in Nigeria</strong> can mean days of travel, lost wages, and fees that outweigh the claim itself. The result is a quiet injustice: a system that exists for everyone but is practically reachable by few.',
            },
            {
                type: 'p',
                html: '<strong>Tech justice</strong> is our answer to that. It is the simple idea that technology — the same tools reshaping banking and commerce — should be turned toward widening access to justice, not just toward profit.',
            },
            { type: 'h2', text: 'What tech justice looks like in practice' },
            {
                type: 'p',
                html: 'For us, <strong>tech advocacy</strong> is not a buzzword; it is a set of concrete tools. <strong>Digital legal advocacy</strong> means using software to do the things that used to require standing in a queue: understanding your rights, preparing the correct documents, and reaching a lawyer without first reaching a courthouse.',
            },
            {
                type: 'p',
                html: 'One of the clearest examples is being able to <strong>file a legal complaint online</strong>. Instead of giving up because the nearest office is hours away, a citizen can start the process from a phone — and have it routed to a body or a lawyer that can act.',
            },
            {
                type: 'ul',
                items: [
                    'Understand your rights in plain language, before you spend a naira',
                    'Draft petitions and complaint letters that institutions have to respond to',
                    'Track the progress of court cases instead of guessing where a file has gone',
                    'Connect to a lawyer for the matters that genuinely need one',
                ],
            },
            { type: 'h2', text: 'Technology that remembers who it is for' },
            {
                type: 'p',
                html: 'Citizens’ Gavel has spent years proving this model. Justice Clock, built with a state government, brought transparency to how case files move through the system. Our interventions have helped thousands of people who would otherwise have been shut out of the courts entirely. Podus is the next step: putting that capability directly in citizens’ hands.',
            },
            {
                type: 'p',
                html: 'The point of tech justice is not to replace judges, lawyers or courts. It is to make sure the <strong>justice</strong> they administer is reachable by the people it was always meant to serve. That is the kind of <strong>advocacy</strong> we are building — one accessible tool at a time.',
            },
            {
                type: 'p',
                html: 'Explore what we are building at <a href="https://podus.org" target="_blank" rel="noopener">podus.org</a>.',
            },
        ],
    },
];

export const getPost = (slug: string): Post | undefined => POSTS.find((p) => p.slug === slug);
